import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  Text,
  VStack,
  Box,
  Flex,
  Tooltip,
  useToast,
} from "@chakra-ui/react";
import { getAllPromoCodes } from "../../graphql/queries";
import { CopyIcon } from "@chakra-ui/icons";
import { useEffect, useState, useCallback, useRef } from "react";
import { API, graphqlOperation } from "aws-amplify";
import awsExports from "../../aws-exports";
import React from "react";
import { connect } from "react-redux";

/* ─── inline SVGs ─── */
const CHAMPAGNE_SVG = encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 36 120">
  <!-- foil top -->
  <rect x="13" y="0" width="10" height="14" rx="3" fill="#c9a84c"/>
  <rect x="11" y="10" width="14" height="6" rx="2" fill="#b8932a"/>
  <!-- wire cage -->
  <line x1="14" y1="10" x2="12" y2="22" stroke="#888" stroke-width="1"/>
  <line x1="22" y1="10" x2="24" y2="22" stroke="#888" stroke-width="1"/>
  <ellipse cx="18" cy="22" rx="6" ry="2" fill="none" stroke="#888" stroke-width="1"/>
  <!-- neck -->
  <rect x="15" y="16" width="6" height="20" rx="2" fill="#2e6b45"/>
  <!-- shoulder -->
  <ellipse cx="18" cy="36" rx="13" ry="5" fill="#2e6b45"/>
  <!-- body -->
  <rect x="5" y="36" width="26" height="68" rx="8" fill="#2e6b45"/>
  <!-- label -->
  <rect x="8" y="48" width="20" height="30" rx="4" fill="#f5f0e0" opacity=".92"/>
  <rect x="10" y="51" width="16" height="3" rx="1" fill="#c9a84c" opacity=".9"/>
  <rect x="11" y="56" width="14" height="2" rx="1" fill="#999" opacity=".5"/>
  <rect x="11" y="60" width="12" height="2" rx="1" fill="#999" opacity=".4"/>
  <!-- shine -->
  <rect x="7" y="38" width="4" height="50" rx="2" fill="#fff" opacity=".1"/>
  <!-- base -->
  <ellipse cx="18" cy="104" rx="13" ry="4" fill="#1d4a2f" opacity=".5"/>
</svg>`);

const WINE_SVG = encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 28 108">
  <rect x="10" y="0" width="8" height="10" rx="2" fill="#c8a96e"/>
  <rect x="12" y="10" width="4" height="32" rx="2" fill="#6b1a2a"/>
  <ellipse cx="14" cy="42" rx="11" ry="5" fill="#6b1a2a"/>
  <rect x="3" y="42" width="22" height="54" rx="8" fill="#6b1a2a"/>
  <rect x="6" y="54" width="16" height="24" rx="3" fill="#f5e6d3" opacity=".9"/>
  <rect x="8" y="57" width="12" height="2" rx="1" fill="#6b1a2a" opacity=".7"/>
  <rect x="9" y="61" width="10" height="2" rx="1" fill="#aaa" opacity=".5"/>
  <rect x="7" y="44" width="3" height="36" rx="2" fill="#fff" opacity=".1"/>
</svg>`);

const BEER_SVG = encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 34 98">
  <circle cx="17" cy="5" r="6" fill="#8B0000"/>
  <rect x="11" y="5" width="12" height="4" fill="#8B0000"/>
  <rect x="13" y="9" width="8" height="14" rx="2" fill="#d4880a"/>
  <ellipse cx="17" cy="23" rx="13" ry="5" fill="#d4880a"/>
  <rect x="4" y="23" width="26" height="62" rx="6" fill="#d4880a"/>
  <ellipse cx="17" cy="24" rx="13" ry="4" fill="#fff9e6" opacity=".6"/>
  <rect x="7" y="34" width="20" height="28" rx="3" fill="#fff3cc" opacity=".9"/>
  <rect x="9" y="37" width="16" height="3" rx="1" fill="#8B0000" opacity=".8"/>
  <rect x="10" y="43" width="14" height="2" rx="1" fill="#888" opacity=".5"/>
  <rect x="6" y="25" width="4" height="44" rx="2" fill="#fff" opacity=".14"/>
</svg>`);

/* ─── CSS injection ─── */
const injectStyles = () => {
  if (document.getElementById("champ-modal-css")) return;
  const s = document.createElement("style");
  s.id = "champ-modal-css";
  s.innerHTML = `
    @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Outfit:wght@400;600;700&display=swap');

    /* ── base anims ── */
    @keyframes cm-shimmer   { 0%{background-position:-600px 0} 100%{background-position:600px 0} }
    @keyframes cm-slideUp   { from{opacity:0;transform:translateY(26px)} to{opacity:1;transform:translateY(0)} }
    @keyframes cm-burst     { 0%{opacity:0;transform:scale(.3) rotate(-8deg)} 60%{opacity:1;transform:scale(1.06) rotate(2deg)} 100%{opacity:1;transform:scale(1) rotate(0)} }
    @keyframes cm-codeIn    { from{opacity:0;letter-spacing:-.35em} to{opacity:1;letter-spacing:.18em} }
    @keyframes cm-glow      { 0%,100%{text-shadow:0 0 14px #f0c040,0 0 32px #f0c040} 50%{text-shadow:0 0 28px #ffe066,0 0 60px #f0c040,0 0 90px #ffb300} }
    @keyframes cm-pulse     { 0%,100%{box-shadow:0 4px 18px rgba(183,38,24,.5),0 0 0 0 rgba(183,38,24,.55)} 65%{box-shadow:0 4px 18px rgba(183,38,24,.4),0 0 0 16px rgba(183,38,24,0)} }
    @keyframes cm-float     { 0%,100%{transform:translateY(0) rotate(-1deg)} 50%{transform:translateY(-8px) rotate(1deg)} }
    @keyframes cm-badgeBounce { 0%,100%{transform:scale(1) rotate(-12deg)} 50%{transform:scale(1.1) rotate(-8deg)} }
    @keyframes cm-spin      { to{transform:rotate(360deg)} }
    @keyframes cm-twinkle   { 0%,100%{opacity:.2;transform:scale(.7)} 50%{opacity:1;transform:scale(1.3)} }
    @keyframes cm-starPop   { 0%{opacity:0;transform:scale(0) rotate(0)} 50%{opacity:1;transform:scale(1.3) rotate(180deg)} 100%{opacity:.6;transform:scale(1) rotate(360deg)} }

    /* ── champagne cork pop ── */
    @keyframes cm-corkFly {
      0%  { transform:translate(0,0) rotate(0deg);   opacity:1; }
      20% { transform:translate(-12px,-60px) rotate(-40deg); opacity:1; }
      60% { transform:translate(-30px,-160px) rotate(-90deg); opacity:.8; }
      100%{ transform:translate(-55px,-280px) rotate(-140deg); opacity:0; }
    }

    /* ── champagne stream (arc up then fall) ── */
    @keyframes cm-stream {
      0%   { transform:translate(0,0) scale(1);    opacity:0; }
      5%   { opacity:1; }
      40%  { opacity:1; }
      100% { opacity:0; }
    }

    /* ── bubble float up ── */
    @keyframes cm-bubble {
      0%   { transform:translateY(0)   scale(1);   opacity:.85; }
      80%  { opacity:.5; }
      100% { transform:translateY(-520px) scale(.3); opacity:0; }
    }

    /* ── falling bottles ── */
    @keyframes cm-fall {
      0%  { transform:translateY(-130px) rotate(0deg); opacity:0; }
      6%  { opacity:.85; }
      92% { opacity:.85; }
      100%{ transform:translateY(600px) rotate(340deg); opacity:0; }
    }

    /* ── liquid arc drops ── */
    @keyframes cm-arc {
      0%   { transform:translate(0,0); opacity:1; }
      100% { opacity:0; }
    }

    /* ── star/sparkle ── */
    @keyframes cm-sparkle {
      0%,100%{ opacity:0; transform:scale(0) rotate(0deg); }
      50%    { opacity:1; transform:scale(1) rotate(180deg); }
    }

    .cm-bubble {
      position:absolute; border-radius:50%;
      background:radial-gradient(circle at 35% 35%, rgba(255,255,255,.9), rgba(200,220,255,.3));
      border:1px solid rgba(255,255,255,.4);
      pointer-events:none;
      animation:cm-bubble linear infinite;
    }
    .cm-bottle {
      position:absolute; pointer-events:none;
      animation:cm-fall linear infinite;
      filter:drop-shadow(0 3px 8px rgba(0,0,0,.4));
    }
    .cm-sparkle-star {
      position:absolute; pointer-events:none;
      animation:cm-sparkle ease-in-out infinite;
      font-size:16px; line-height:1;
    }
  `;
  document.head.appendChild(s);
};

/* ─── Champagne Burst Canvas (pure CSS/DOM) ─── */
const STREAM_COUNT = 22;
const BUBBLE_COUNT = 35;

const ChampagneBurst = ({ triggered }) => {
  /* streams radiate from bottle neck position (centre-bottom area) */
  const streams = Array.from({ length: STREAM_COUNT }, (_, i) => {
    const angle  = -160 + (i / (STREAM_COUNT - 1)) * 140; // -160° to -20° (fan upward)
    const rad    = (angle * Math.PI) / 180;
    const dist   = 120 + Math.random() * 200;
    const endX   = Math.cos(rad) * dist;
    const endY   = Math.sin(rad) * dist;
    const size   = 4 + Math.random() * 8;
    const colors = ["#f5d76e","#ffe066","#fff7cc","#c9f0ff","#ffffff","#ffd700"];
    const color  = colors[i % colors.length];
    const dur    = 0.6 + Math.random() * 0.8;
    const delay  = Math.random() * 0.3;
    return { endX, endY, size, color, dur, delay };
  });

  const bubbles = Array.from({ length: BUBBLE_COUNT }, (_, i) => ({
    left:  `${25 + Math.random() * 50}%`,
    size:  `${4 + Math.random() * 10}px`,
    dur:   `${2 + Math.random() * 3}s`,
    delay: `${Math.random() * 2}s`,
  }));

  return (
    <Box
      position="absolute" inset="0" overflow="hidden"
      pointerEvents="none" zIndex={5}
      style={{ opacity: triggered ? 1 : 0, transition: "opacity .3s" }}
    >
      {/* bubbles */}
      {bubbles.map((b, i) => (
        <div key={i} className="cm-bubble" style={{
          left: b.left, bottom: "30%",
          width: b.size, height: b.size,
          animationDuration: b.dur,
          animationDelay: triggered ? b.delay : "9999s",
        }} />
      ))}

      {/* stream drops — pure CSS transforms with keyframe override via style */}
      {triggered && streams.map((st, i) => (
        <div key={i} style={{
          position: "absolute",
          bottom: "28%",
          left:   "48%",
          width:  `${st.size}px`,
          height: `${st.size * 1.6}px`,
          borderRadius: "50%",
          backgroundColor: st.color,
          boxShadow: `0 0 6px ${st.color}`,
          pointerEvents: "none",
          animation: `cm-stream ${st.dur}s ease-out ${st.delay}s 3 both`,
          /* CSS custom property trick: animate to endX/endY via inline keyframes */
          transform: `translate(${st.endX}px, ${st.endY}px)`,
          opacity: 0,
        }} />
      ))}

      {/* sparkle stars */}
      {triggered && ["✦","✧","★","✦","✧","⭐","✦"].map((star,i)=>(
        <div key={i} className="cm-sparkle-star" style={{
          left:  `${15 + i * 12}%`,
          top:   `${8 + (i%3) * 12}%`,
          animationDuration: `${1.2 + i*.3}s`,
          animationDelay:    `${i*.15}s`,
          color: ["#ffd700","#fff7cc","#c9f0ff","#ffe066","#ffffff","#f5d76e","#ffd700"][i],
        }}>{star}</div>
      ))}
    </Box>
  );
};

/* ─── Falling bottles ─── */
const BOTTLES_DATA = [
  { svg: CHAMPAGNE_SVG, w:24, h:88  },
  { svg: WINE_SVG,      w:20, h:78  },
  { svg: BEER_SVG,      w:26, h:70  },
  { svg: CHAMPAGNE_SVG, w:18, h:80  },
];
const STREAMS = [
  {idx:0,left:"3%", dur:"4.2s",delay:"0s",   scale:.9},
  {idx:1,left:"12%",dur:"5.1s",delay:"1.3s", scale:.75},
  {idx:2,left:"21%",dur:"3.8s",delay:"2.6s", scale:.85},
  {idx:3,left:"31%",dur:"4.7s",delay:"0.7s", scale:.7},
  {idx:0,left:"42%",dur:"5.4s",delay:"1.9s", scale:.8},
  {idx:1,left:"54%",dur:"4.0s",delay:"3.1s", scale:.95},
  {idx:2,left:"64%",dur:"4.5s",delay:"0.4s", scale:.7},
  {idx:3,left:"74%",dur:"3.6s",delay:"2.2s", scale:.85},
  {idx:0,left:"84%",dur:"4.9s",delay:"1.6s", scale:.75},
  {idx:1,left:"93%",dur:"4.3s",delay:"2.9s", scale:.8},
];
const FallingBottles = () => STREAMS.map((s,i)=>{
  const b = BOTTLES_DATA[s.idx];
  return (
    <img key={i} className="cm-bottle"
      src={`data:image/svg+xml,${b.svg}`}
      style={{
        left:s.left, top:"-140px",
        width:`${b.w*s.scale}px`, height:`${b.h*s.scale}px`,
        animationDuration:s.dur, animationDelay:s.delay, opacity:0,
      }} alt=""/>
  );
});

/* ─── Twinkling stars ─── */
const STARS=[
  {top:"8%", left:"6%", size:7, dur:"2.1s",del:"0s"},
  {top:"12%",left:"85%",size:6, dur:"1.7s",del:".5s"},
  {top:"55%",left:"4%", size:5, dur:"2.4s",del:".9s"},
  {top:"72%",left:"89%",size:8, dur:"1.9s",del:".3s"},
  {top:"88%",left:"18%",size:6, dur:"2.3s",del:".7s"},
  {top:"40%",left:"93%",size:5, dur:"1.8s",del:"1.1s"},
  {top:"28%",left:"2%", size:4, dur:"2.0s",del:".4s"},
];
const TwinkleStars=()=>STARS.map((st,i)=>(
  <div key={i} className="cm-sparkle-star" style={{
    top:st.top, left:st.left, fontSize:`${st.size+6}px`,
    animationDuration:st.dur, animationDelay:st.del, color:"#ffd700",
  }}>✦</div>
));

/* ════════════════════════════════════════
   MAIN COMPONENT
   ════════════════════════════════════════ */
const CouponsModal = ({ isOpen, onClose, user, assignedStoreId }) => {
  useEffect(()=>{ injectStyles(); },[]);

  const [bestCoupon,  setBestCoupon]  = useState(null);
  const [copied,      setCopied]      = useState(false);
  const [champFired,  setChampFired]  = useState(false);
  const champTimer = useRef(null);
  const toast = useToast();

  /* fire champagne burst on open */
  useEffect(()=>{
    if(isOpen){
      setChampFired(false);
      setTimeout(()=>setChampFired(true), 350);
      champTimer.current = setTimeout(()=>setChampFired(false), 3500);
    }
    return ()=>clearTimeout(champTimer.current);
  },[isOpen]);

  const fetchPromoCodes = useCallback(async()=>{
    try{
      const op = graphqlOperation(getAllPromoCodes,{
        id: assignedStoreId?[assignedStoreId]:[],
        userId: user?.userId||null,
      });
      const res = await API.graphql({...op,authMode:"API_KEY",authToken:awsExports.aws_appsync_apiKey});
      const coupons = res?.data?.getAllPromoCodes?.Items||[];
      if(coupons.length>0)
        setBestCoupon(coupons.reduce((m,c)=>(c.maxCouponValue||0)>(m.maxCouponValue||0)?c:m));
    }catch(e){console.error(e);}
  },[assignedStoreId,user?.userId]);

  useEffect(()=>{
    if(isOpen){setBestCoupon(null);setCopied(false);fetchPromoCodes();}
  },[isOpen]);

  const handleCopy=()=>{
    if(!bestCoupon||copied) return;
    /* re-fire burst on copy */
    setChampFired(false);
    setTimeout(()=>setChampFired(true),50);
    setTimeout(()=>setChampFired(false),3200);

    navigator.clipboard.writeText(bestCoupon.promoCode).then(()=>{
      setCopied(true);
      toast({title:`🥂 "${bestCoupon.promoCode}" copied!`,description:"Apply at checkout to save!",status:"success",duration:1500,position:"top"});
      setTimeout(()=>{onClose();window.location.href="/";},1600);
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="xl">
      <ModalOverlay bg="rgba(18,2,2,.88)" backdropFilter="blur(7px)"/>

      <ModalContent
        borderRadius="22px" overflow="hidden"
        maxW="720px" w="92%"
        bg="transparent" boxShadow="none" position="relative"
      >
        {/* animated gold-teal gradient border */}
        <Box
          position="absolute" inset="-3px" borderRadius="24px" zIndex={-1}
          background="linear-gradient(135deg,#B72618,#f0c040,#e8453c,#f0c040,#B72618)"
          backgroundSize="300% 300%"
          style={{animation:"cm-shimmer 4s linear infinite"}}
        />

        {/* card — deep navy-teal */}
        <Box
          borderRadius="22px" overflow="hidden" position="relative"
          bg="linear-gradient(160deg,#1a0202 0%,#2d0505 40%,#3a0808 75%,#1f0303 100%)"
        >
          {/* ── champagne burst layer ── */}
          <ChampagneBurst triggered={champFired}/>

          {/* ── falling bottles ── */}
          <Box position="absolute" inset="0" overflow="hidden" zIndex={1} pointerEvents="none">
            <FallingBottles/>
          </Box>

          {/* ── twinkling stars ── */}
          <Box position="absolute" inset="0" pointerEvents="none" zIndex={1}>
            <TwinkleStars/>
          </Box>

          {/* ── subtle radial glow ── */}
          <Box
            position="absolute" inset="0" pointerEvents="none" zIndex={0}
            background="radial-gradient(ellipse 70% 55% at 50% 35%, rgba(183,38,24,.18) 0%, transparent 70%)"
          />

          {/* close */}
          <ModalCloseButton
            color="rgba(255,255,255,.6)" fontSize="16px" top="16px" right="16px"
            zIndex={20} borderRadius="full"
            _hover={{color:"white",bg:"rgba(240,192,64,.2)"}}
          />

          <ModalBody p={{base:"40px 22px 44px",md:"52px 56px 56px"}} position="relative" zIndex={2}>
            <VStack spacing="22px">

              {/* pill badge */}
              <Box
                bg="linear-gradient(90deg,#B72618,#e8453c)"
                px="18px" py="7px" borderRadius="full"
                boxShadow="0 4px 22px rgba(183,38,24,.5)"
                style={{animation:"cm-slideUp .5s ease both"}}
              >
                <Text fontFamily="'Outfit',sans-serif" fontSize="11px" fontWeight="700"
                  color="white" letterSpacing=".15em" textTransform="uppercase">
                  🥂 &nbsp;Exclusive Welcome Offer
                </Text>
              </Box>

              {/* champagne bottle emoji + headline */}
              <VStack spacing="6px" textAlign="center"
                style={{animation:"cm-burst .7s cubic-bezier(.36,.07,.19,.97) .15s both"}}>
                <Text fontSize="52px" lineHeight="1"
                  style={{animation:"cm-float 4s ease-in-out infinite,cm-slideUp .5s ease .1s both"}}>
                  🍾
                </Text>
                <Text
                  fontFamily="'Bebas Neue',sans-serif"
                  fontSize={{base:"52px",md:"70px"}}
                  lineHeight="1" color="white" textTransform="uppercase"
                  style={{animation:"cm-glow 3s ease-in-out infinite"}}
                >
                  You're In!
                </Text>
                <Text
                  fontFamily="'Bebas Neue',sans-serif"
                  fontSize={{base:"26px",md:"36px"}}
                  color="#f0c040" letterSpacing=".06em"
                >
                  Here's Your Special Deal
                </Text>
              </VStack>

              {/* coupon card */}
              {bestCoupon && (
                <Box w="100%" position="relative"
                  style={{animation:"cm-float 5s ease-in-out 1s infinite,cm-slideUp .6s ease .35s both"}}>

                  {/* save badge */}
                  <Box
                    position="absolute" top="-18px" right="-8px" zIndex={3}
                    bg="linear-gradient(135deg,#f0c040,#ff9800)"
                    w="72px" h="72px" borderRadius="full"
                    display="flex" flexDirection="column" alignItems="center" justifyContent="center"
                    boxShadow="0 4px 22px rgba(240,192,64,.65)"
                    style={{animation:"cm-badgeBounce 2s ease-in-out infinite"}}
                  >
                    <Text fontFamily="'Bebas Neue',sans-serif" fontSize="11px" color="#1a0800" lineHeight="1">SAVE</Text>
                    <Text fontFamily="'Bebas Neue',sans-serif" fontSize="24px" color="#1a0800" lineHeight="1">${bestCoupon.maxCouponValue}</Text>
                  </Box>

                  {/* ticket */}
                  <Box
                    bg="linear-gradient(135deg,rgba(183,38,24,.12),rgba(232,69,60,.08))"
                    border="2px dashed rgba(240,192,64,.5)"
                    borderRadius="16px"
                    p={{base:"24px 18px",md:"30px 40px"}}
                    backdropFilter="blur(10px)"
                    boxShadow="0 0 40px rgba(183,38,24,.15),inset 0 1px 0 rgba(255,255,255,.07)"
                    position="relative" overflow="hidden"
                  >
                    {/* shimmer */}
                    <Box
                      position="absolute" inset="0" pointerEvents="none"
                      background="linear-gradient(90deg,transparent,rgba(255,255,255,.05),transparent)"
                      backgroundSize="400px 100%"
                      style={{animation:"cm-shimmer 3s linear infinite"}}
                    />
                    {/* notches */}
                    {["left","right"].map(side=>(
                      <Box key={side} position="absolute" top="50%" {...{[side]:"-12px"}}
                        transform="translateY(-50%)" w="24px" h="24px" borderRadius="full"
                        bg="linear-gradient(135deg,#1a0202,#2d0505)"
                      />
                    ))}

                    <VStack spacing="14px" position="relative">
                      <Text fontFamily="'Outfit',sans-serif" fontSize="11px" fontWeight="700"
                        color="rgba(240,192,64,.85)" letterSpacing=".2em" textTransform="uppercase">
                        Your Coupon Code
                      </Text>

                      <Flex alignItems="center" gap="14px" wrap="wrap" justify="center">
                        <Text
                          fontFamily="'Bebas Neue',sans-serif"
                          fontSize={{base:"40px",md:"56px"}}
                          color="white" letterSpacing=".2em"
                          textShadow="0 0 20px rgba(240,192,64,.4)"
                          style={{animation:"cm-codeIn .9s ease .55s both"}}
                        >
                          {bestCoupon.promoCode}
                        </Text>

                        <Tooltip label="Tap to copy & pop the champagne! 🍾" hasArrow bg="#B72618" borderRadius="8px">
                          <Box
                            cursor="pointer" onClick={handleCopy}
                            bg={copied
                              ? "linear-gradient(135deg,#22c55e,#16a34a)"
                              : "linear-gradient(135deg,#B72618,#e8453c)"}
                            borderRadius="12px" px="20px" py="12px"
                            display="flex" alignItems="center" gap="8px"
                            style={{animation:"cm-pulse 2.5s ease-in-out infinite"}}
                            transition="all .3s ease"
                            _hover={{transform:"scale(1.06)"}}
                          >
                            <CopyIcon color="white" fontSize="18px"/>
                            <Text fontFamily="'Outfit',sans-serif" fontWeight="700" fontSize="14px"
                              color="white">
                              {copied?"Copied! 🥂":"Copy Code"}
                            </Text>
                          </Box>
                        </Tooltip>
                      </Flex>

                      {/* divider */}
                      <Box w="80%" h="1px" mx="auto"
                        background="linear-gradient(90deg,transparent,rgba(240,192,64,.4),transparent)"/>

                      <Flex align="center" gap="8px" wrap="wrap" justify="center">
                        <Text fontSize="18px">🍾</Text>
                        <Text fontFamily="'Outfit',sans-serif" fontSize="15px" color="rgba(255,255,255,.65)">
                          Save up to{" "}
                          <Text as="span" color="#f0c040" fontWeight="700" fontSize="18px">${bestCoupon.maxCouponValue}</Text>
                          {" "}on your order
                        </Text>
                      </Flex>
                    </VStack>
                  </Box>
                </Box>
              )}

              {/* hint */}
              <Text fontFamily="'Outfit',sans-serif" fontSize="13px"
                color="rgba(255,255,255,.35)" textAlign="center"
                style={{animation:"cm-slideUp .5s ease .6s both"}}>
                🥂 Copy your code — the celebration begins at checkout!
              </Text>

            </VStack>
          </ModalBody>
        </Box>
      </ModalContent>
    </Modal>
  );
};

const stateMapper=(state)=>({
  storageCart:state.cart.cart,
  user:state.auth?.user,
});
export default connect(stateMapper,()=>({}))(CouponsModal);